// src/modules/estimates/ServiceModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../../components/shared/Modal';
import Input from '../../components/shared/Input';
import SearchableSelect from '../../components/shared/SearchableSelect';
import Button from '../../components/shared/Button';
import MaskedInput from '../../components/shared/MaskedInput';
import AreaMeasurementModal from './AreaMeasurementModal'; // Import the modal for area measurement

const ServiceModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(3)};
`;

const AreaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(2)};
  margin-top: ${(props) => props.theme.spacing(3)};
`;

const AreaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing(2)};
  border: 1px solid ${(props) => props.theme.colors.inputGray};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.surface};
  width: 100%;

  .area-name {
    font-size: 16px;
  }

  .area-value {
    font-weight: bold;
  }

  .remove-button {
    cursor: pointer;
    color: ${(props) => props.theme.colors.error};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const UNIT_OPTIONS = [
  { value: 'sqft', label: 'Square Feet', short: 'sq ft', priceLabel: 'Price per sq ft' },
  { value: 'lnft', label: 'Linear Feet', short: 'lin ft', priceLabel: 'Price per lin ft' },
  { value: 'yd', label: 'Yards', short: 'yd', priceLabel: 'Price per yard' },
  { value: 'unit', label: 'Unit', short: 'unit', priceLabel: 'Price per unit' },
];

export const getUnitInfo = (unitValue) => UNIT_OPTIONS.find(u => u.value === unitValue) || UNIT_OPTIONS[0];

const ServiceModal = ({ isOpen, onClose, onSave, availableServices, existingService }) => {
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [pricePerSqft, setPricePerSqft] = useState(0);
  const [totalArea, setTotalArea] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceName, setServiceName] = useState('');
  const [measurementUnit, setMeasurementUnit] = useState('sqft');
  const [areaMeasurements, setAreaMeasurements] = useState([]);
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [isManualPriceEdit, setIsManualPriceEdit] = useState(false);

  const unitInfo = getUnitInfo(measurementUnit);

  // Reset form values whenever the modal opens or an existing service is passed in
  useEffect(() => {
    if (existingService) {
      setSelectedServiceId(existingService.serviceId);
      setPricePerSqft(existingService.pricePerSqft || 0);
      setTotalArea(existingService.totalArea || 0);
      setTotalPrice(existingService.totalPrice || 0);
      setServiceName(existingService.serviceName || '');
      setMeasurementUnit(existingService.measurementUnit || 'sqft');
      setAreaMeasurements(existingService.areaMeasurements || []);
      setIsManualPriceEdit(false);
    } else {
      resetServiceForm();
    }
  }, [existingService]);

  // Handle service selection and fetch the price from the selected service
  // Receives the raw value from SearchableSelect (not a change event).
  const handleServiceSelect = (serviceId) => {
    setSelectedServiceId(serviceId);

    // Find the selected service from the available services
    const selectedService = availableServices.find((service) => service.id === parseInt(serviceId, 10));
    if (selectedService) {
      console.log(selectedService);
      setPricePerSqft(selectedService.price_per_sqft || 0); // Set the price per sqft from the selected service
      setServiceName(selectedService.service_name);
      calculateTotalPrice(totalArea, selectedService.price_per_sqft || 0);
    }
  };

  // Handle input changes for the area and calculate total price
  const handleAreaChange = (e) => {
    const areaValue = parseFloat(e.target.value) || 0; // Parse to float, default to 0
    setTotalArea(areaValue);
    calculateTotalPrice(areaValue, pricePerSqft);
  };

  // Calculate total price based on area and price per sqft
  const calculateTotalPrice = (area, price) => {
    const total = area * price;
    setTotalPrice(total ? total.toFixed(2) : 0); // Ensure total price is a formatted string with 2 decimals
  };

  // Handle price per sqft change and update total price accordingly
  const handlePriceChange = (e) => {
    const priceValue = parseFloat(e.target.value) || 0; // Parse to float, default to 0
    setPricePerSqft(priceValue);
    calculateTotalPrice(totalArea, priceValue);
  };

  // Handle manual total price change
  const handleTotalPriceChange = (e) => {
    const totalValue = parseFloat(e.target.value) || 0;
    setTotalPrice(totalValue);
    setIsManualPriceEdit(true); // Mark that user manually edited the price
  };

  // Reset the manual price edit flag when recalculating
  const recalculatePrice = () => {
    setIsManualPriceEdit(false);
    calculateTotalPrice(totalArea, pricePerSqft);
  };

const handleAddAreaMeasurement = (newAreas) => {
    const updatedAreas = [...areaMeasurements, ...newAreas];
    console.log(updatedAreas);
    setAreaMeasurements(updatedAreas);
    updateTotalArea(updatedAreas);
    setAreaModalOpen(false);
  };

  // Remove an area measurement
  const handleRemoveArea = (index) => {
    const updatedAreas = [...areaMeasurements];
    updatedAreas.splice(index, 1);
    setAreaMeasurements(updatedAreas);
    updateTotalArea(updatedAreas);
  };

  // Update total area and total price based on all area measurements
  const updateTotalArea = (areas) => {
    const totalAreaValue = areas.reduce((sum, area) => sum + parseFloat(area.totalArea  || 0), 0);
    console.log("Total Calc: ",totalAreaValue);
    setTotalArea(totalAreaValue);
    calculateTotalPrice(totalAreaValue, pricePerSqft);
  };

  // Save the selected service with all its details back to the parent form
  const handleSave = () => {
    if (selectedServiceId && pricePerSqft > 0 && totalArea > 0) {
      onSave({
        serviceId: selectedServiceId,
        serviceName,
        pricePerSqft: parseFloat(pricePerSqft),
        totalArea: parseFloat(totalArea),
        totalPrice: parseFloat(totalPrice),
        measurementUnit,
        areaMeasurements,
      });
      resetServiceForm(); // Reset form after saving
      onClose(); // Close the modal
    } else {
      alert('Please complete all required fields before saving.');
    }
  };

  // Reset form values
  const resetServiceForm = () => {
    setSelectedServiceId('');
    setPricePerSqft(0);
    setTotalArea(0);
    setTotalPrice(0);
    setServiceName('');
    setMeasurementUnit('sqft');
    setAreaMeasurements([]);
    setIsManualPriceEdit(false);
  };

  return (
    <>
      <Modal isOpen={isOpen} onConfirm={handleSave} onClose={onClose} onCancel={onClose} title="Select Service" disableBackdropClose>
        <ServiceModalWrapper>
          <SearchableSelect
            label="Service"
            value={selectedServiceId}
            options={availableServices.map((service) => ({
              // String, so selectedServiceId and the saved payload keep the exact
              // type the old native <select> produced (e.target.value).
              value: String(service.id),
              label: service.service_name,
            }))}
            onChange={handleServiceSelect}
            placeholder="Search services…"
            required
          />

          <SearchableSelect
            label="Measurement Unit"
            value={measurementUnit}
            options={UNIT_OPTIONS.map((u) => ({ value: u.value, label: u.label }))}
            onChange={(v) => setMeasurementUnit(v)}
            placeholder="Search units…"
          />

          <MaskedInput
            label={unitInfo.priceLabel}
            mask="currency"
            value={pricePerSqft}
            onChange={handlePriceChange}
            placeholder={`Enter ${unitInfo.priceLabel.toLowerCase()}`}
            required
          />

          <Input
            label={`Total Area (${unitInfo.short})`}
            type="number"
            value={totalArea}
            onChange={handleAreaChange}
            placeholder={`Enter total area in ${unitInfo.short}`}
            required
          />

          {/* <MaskedInput
            label="Total Price ($)"
            mask="currency"
            value={totalPrice}
            placeholder="Total price calculated"
            required
          /> */}

          {/* <Input
            label="Total Price ($)"
            type="number"
            value={totalPrice}
            placeholder="Total price calculated"
            required
          /> */}

          {/* Updated Total Price field with MaskedInput and manual edit capability */}
          <div style={{ position: 'relative' }}>
            <MaskedInput
              label="Total Price ($)"
              mask="currency"
              value={totalPrice}
              onChange={handleTotalPriceChange}
              placeholder="Total price"
              required
            />
            {isManualPriceEdit && (
              <div style={{ 
                fontSize: '12px', 
                color: '#f39c12', 
                marginTop: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>Manual override active</span>
                <button
                  type="button"
                  onClick={recalculatePrice}
                  style={{
                    background: 'none',
                    border: '1px solid #f39c12',
                    color: '#f39c12',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  Recalculate
                </button>
              </div>
            )}
          </div>

          <Button type="button" onClick={() => setAreaModalOpen(true)}>Measurement Tool</Button>

          {areaMeasurements.length > 0 && (
            <AreaList>
              {areaMeasurements.map((area, index) => (
                <AreaItem key={index}>
                  <span className="area-name">{area.name}</span>
                  <span className="area-value">{area.totalArea} {unitInfo.short}</span>
                  <span className="remove-button" onClick={() => handleRemoveArea(index)}>Remove</span>
                </AreaItem>
              ))}
            </AreaList>
          )}
        </ServiceModalWrapper>
      </Modal>

      {/* Area Measurement Modal */}
      {areaModalOpen && (
        <AreaMeasurementModal
          isOpen={areaModalOpen}
          onSave={handleAddAreaMeasurement}
          onClose={() => setAreaModalOpen(false)}
          measurementUnit={measurementUnit}
        />
      )}
    </>
  );
};

export default ServiceModal;
